import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import {
  eyeIcon,
  PresentationIcon,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import React from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { LanguageList } from "../app-language/LanguageList";
import { useAuth } from "../auth/AuthContext";
import { usersDialogOpenAtom } from "../auth/UserManagementDialog";
import { useAtom } from "../app-jotai";
import {
  libraryApi,
  type BackupBundle,
  type CanvasScene,
} from "../data/libraryApi";
import {
  EMPTY_EXPORT,
  downloadJson,
  exportAllToPdfZip,
  exportSceneToPdf,
} from "../data/exportPdf";

import { saveDebugState } from "./DebugCanvas";
import { gridSettingsOpenAtom } from "./GridSettingsDialog";

const usersIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const logoutIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const pdfIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M9 15h1.5a1.5 1.5 0 0 0 0-3H9v6" />
    <path d="M14 18v-6h1.5a2 2 0 0 1 0 4H14" />
  </svg>
);

const backupIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const gridIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="1" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
  </svg>
);

const importIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  setTheme: (theme: Theme | "system") => void;
  refresh: () => void;
  onLibraryChanged: () => void;
  onRequestExitPresentation: () => void;
}> = React.memo((props) => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [, setUsersOpen] = useAtom(usersDialogOpenAtom);
  const [, setGridOpen] = useAtom(gridSettingsOpenAtom);
  const excalidrawAPI = useExcalidrawAPI();

  const toast = (message: string) => excalidrawAPI?.setToast({ message });

  const handleSaveAsPdf = async () => {
    if (!excalidrawAPI) {
      return;
    }
    try {
      await exportSceneToPdf(
        excalidrawAPI.getSceneElements(),
        excalidrawAPI.getAppState(),
        excalidrawAPI.getFiles(),
        excalidrawAPI.getName(),
      );
    } catch (e) {
      toast(
        e instanceof Error && e.message === EMPTY_EXPORT
          ? t("fileExtras.nothingToExport")
          : t("fileExtras.pdfError"),
      );
    }
  };

  const handleDownloadBackup = async () => {
    try {
      const bundle = await libraryApi.exportBackup();
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadJson(bundle, `pizzarweb-backup-${stamp}.json`);
    } catch {
      toast(t("fileExtras.backupError"));
    }
  };

  const handleImportBackup = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }
      try {
        const bundle = JSON.parse(await file.text()) as BackupBundle;
        toast(t("fileExtras.importingBackup"));
        const res = await libraryApi.importBackup(bundle);
        props.onLibraryChanged();
        toast(
          t("fileExtras.importedBackup", {
            folders: res.folders,
            canvases: res.canvases,
          }),
        );
      } catch {
        toast(t("fileExtras.backupError"));
      }
    };
    input.click();
  };

  const togglePresentation = () => {
    if (!excalidrawAPI) {
      return;
    }
    if (excalidrawAPI.getAppState().presentationModeEnabled) {
      // Exit goes through the app's modal (so changes aren't silently lost).
      props.onRequestExitPresentation();
    } else {
      excalidrawAPI.updateScene({
        appState: { presentationModeEnabled: true },
      });
    }
  };

  const handleExportAllPdf = async () => {
    try {
      toast(t("fileExtras.preparingPdfs"));
      const canvases = await libraryApi.listCanvases();
      const items: { name: string; scene: CanvasScene }[] = [];
      for (const c of canvases) {
        const scene = await libraryApi.getScene(c.id);
        items.push({ name: c.name, scene });
      }
      const count = await exportAllToPdfZip(items);
      toast(t("fileExtras.pdfsReady", { count }));
    } catch (e) {
      toast(
        e instanceof Error && e.message === EMPTY_EXPORT
          ? t("fileExtras.nothingToExport")
          : t("fileExtras.pdfError"),
      );
    }
  };

  return (
    <MainMenu>
      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />
      <MainMenu.Item icon={pdfIcon} onSelect={() => void handleSaveAsPdf()}>
        {t("fileExtras.saveAsPdf")}
      </MainMenu.Item>
      <MainMenu.Item
        icon={PresentationIcon}
        onSelect={() => togglePresentation()}
      >
        {t("labels.presentationMode")}
      </MainMenu.Item>
      {props.isCollabEnabled && (
        <MainMenu.DefaultItems.LiveCollaborationTrigger
          isCollaborating={props.isCollaborating}
          onSelect={() => props.onCollabDialogOpen()}
        />
      )}
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      <MainMenu.Separator />
      <MainMenu.Item
        icon={backupIcon}
        onSelect={() => void handleDownloadBackup()}
      >
        {t("fileExtras.downloadBackup")}
      </MainMenu.Item>
      <MainMenu.Item icon={importIcon} onSelect={() => handleImportBackup()}>
        {t("fileExtras.importBackup")}
      </MainMenu.Item>
      <MainMenu.Item icon={pdfIcon} onSelect={() => void handleExportAllPdf()}>
        {t("fileExtras.exportAllPdf")}
      </MainMenu.Item>
      <MainMenu.DefaultItems.Socials />
      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
      <MainMenu.DefaultItems.Preferences />
      <MainMenu.DefaultItems.ToggleTheme
        allowSystemTheme
        theme={props.theme}
        onSelect={props.setTheme}
      />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
      <MainMenu.Item icon={gridIcon} onSelect={() => setGridOpen(true)}>
        {t("grid.title")}
      </MainMenu.Item>
      <MainMenu.Separator />
      {user.role === "admin" && (
        <MainMenu.Item icon={usersIcon} onSelect={() => setUsersOpen(true)}>
          {t("auth.users")}
        </MainMenu.Item>
      )}
      <MainMenu.Item icon={logoutIcon} onSelect={() => void logout()}>
        {`${t("auth.logout")} · ${user.username}`}
      </MainMenu.Item>
    </MainMenu>
  );
});
